import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import { ArrowLeft, Images, Sparkles, Upload, X, Check, ScanSearch, FolderOpen } from 'lucide-react';
import { HudButton, HudCard } from '../hud';
import { matApi, fileDownloadUrl } from '../../api/materialthequeApi';

/**
 * PhotoSourcePanel — sélection des photos 3D (upload + fichiers projet),
 * détection IA du type de pièce (corrigeable) puis lancement de l'extraction.
 */
export const PhotoSourcePanel = ({ projectId, onBack, onJobCreated }) => {
  const [candidates, setCandidates] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [selected, setSelected] = useState({}); // file_id -> { room_type, label, confidence }
  const [uploading, setUploading] = useState(false);
  const [classifying, setClassifying] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  const refresh = useCallback(async () => {
    const r = await matApi.listPhotoCandidates(projectId);
    setCandidates(r.files || []);
    setRoomTypes(r.room_types || []);
    return r.files || [];
  }, [projectId]);

  useEffect(() => { refresh().catch(() => toast.error('Impossible de charger les fichiers du projet')); }, [refresh]);

  const onDrop = useCallback(async (accepted) => {
    const imgs = accepted.filter((f) => f.type.startsWith('image/'));
    if (!imgs.length) { toast.error('Déposez des images (PNG, JPG, WebP)'); return; }
    setUploading(true);
    try {
      const r = await matApi.uploadPhotos(projectId, imgs);
      const ids = (r.files || []).map((f) => f.file_id);
      setSelected((s) => ({ ...s, ...Object.fromEntries(ids.map((id) => [id, s[id] || { room_type: null }])) }));
      await refresh();
      toast.success(`${ids.length} photo${ids.length > 1 ? 's' : ''} ajoutée${ids.length > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  }, [projectId, refresh]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] }, multiple: true, maxSize: 25 * 1024 * 1024,
  });

  const selectedIds = useMemo(() => Object.keys(selected), [selected]);
  const selectedFiles = useMemo(() => candidates.filter((c) => selected[c.file_id]), [candidates, selected]);
  const library = useMemo(() => candidates.filter((c) => !selected[c.file_id]), [candidates, selected]);
  const undetected = selectedIds.filter((id) => !selected[id]?.room_type);

  const toggle = (c) => setSelected((s) => {
    const n = { ...s };
    if (n[c.file_id]) delete n[c.file_id];
    else n[c.file_id] = { room_type: c.room_type || null, label: c.room_label || null };
    return n;
  });

  const setRoom = (id, room_type) => setSelected((s) => ({ ...s, [id]: { ...s[id], room_type, manual: true } }));

  const classify = async () => {
    const ids = undetected.length ? undetected : selectedIds;
    if (!ids.length) return;
    setClassifying(true);
    try {
      const r = await matApi.classifyPhotos(projectId, ids);
      setSelected((s) => {
        const n = { ...s };
        (r.results || []).forEach((res) => { if (n[res.file_id]) n[res.file_id] = { ...n[res.file_id], room_type: res.room_type, label: res.label, confidence: res.confidence }; });
        return n;
      });
      toast.success('Pièces détectées — vérifiez et corrigez si besoin');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de détection des pièces');
    } finally {
      setClassifying(false);
    }
  };

  const launch = async () => {
    if (!selectedIds.length) return;
    setLaunching(true);
    try {
      const photos = selectedIds.map((id) => ({ file_id: id, room_type: selected[id]?.room_type || 'autre' }));
      const job = await matApi.extractFromPhotos(projectId, photos);
      onJobCreated(job);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur au lancement de l'extraction");
      setLaunching(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-5 hud-stagger" data-testid="materialtheque-photos-panel">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="hud-btn hud-btn-ghost hud-btn-sm" data-testid="materialtheque-photos-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Autre source
        </button>
        <span className="hud-eyebrow" style={{ color: 'var(--c-text-muted)' }}>
          {selectedIds.length} photo{selectedIds.length > 1 ? 's' : ''} sélectionnée{selectedIds.length > 1 ? 's' : ''}
        </span>
      </div>

      <HudCard variant="glass" style={{ padding: 0 }}>
        <div
          {...getRootProps()}
          data-testid="materialtheque-photos-dropzone"
          className="cursor-pointer px-6 py-8 rounded-[20px] flex items-center gap-5 transition-colors"
          style={{ border: `1.5px dashed ${isDragActive ? 'var(--c-gold)' : 'rgba(255,255,255,0.18)'}`, margin: 10, background: isDragActive ? 'rgba(217,160,91,0.06)' : 'transparent' }}
        >
          <input {...getInputProps()} />
          <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0" style={{ background: 'var(--c-gold-soft)', border: '1px solid var(--c-border-accent)' }}>
            {uploading ? <Sparkles className="w-6 h-6 animate-spin" style={{ color: 'var(--c-gold)' }} /> : <Upload className="w-6 h-6" style={{ color: 'var(--c-gold)' }} strokeWidth={1.5} />}
          </div>
          <div className="min-w-0">
            <p className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 17 }}>
              {uploading ? 'Upload en cours…' : 'Glissez vos rendus 3D intérieurs'}
              <span style={{ color: 'var(--c-gold)' }}> (PNG, JPG, WebP · multi)</span>
            </p>
            <p className="text-[12.5px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
              Une photo par type de pièce suffit — séjour, cuisine, chambre, salle de bain…
            </p>
          </div>
          <button
            type="button"
            className="hud-btn hud-btn-secondary hud-btn-sm ml-auto shrink-0"
            onClick={(e) => { e.stopPropagation(); setShowLibrary((v) => !v); }}
            data-testid="materialtheque-photos-toggle-library"
          >
            <FolderOpen className="w-3.5 h-3.5" /> Fichiers du projet ({library.length})
          </button>
        </div>
        {showLibrary && (
          <div className="px-5 pb-5" data-testid="materialtheque-photos-library">
            {library.length === 0 ? (
              <p className="text-[12.5px] py-3" style={{ color: 'var(--c-text-muted)' }}>Aucune autre image dans « Fichiers ».</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-7 gap-2">
                {library.map((c) => (
                  <button key={c.file_id} type="button" onClick={() => toggle(c)} title={c.filename} data-testid={`materialtheque-library-file-${c.file_id}`}
                    className="relative aspect-square rounded-lg overflow-hidden group" style={{ border: '1px solid var(--c-border)' }}>
                    <img src={fileDownloadUrl(c.file_id)} alt={c.filename} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" loading="lazy" />
                    <span className="absolute bottom-0 inset-x-0 text-[9px] px-1 py-0.5 truncate" style={{ background: 'rgba(0,0,0,0.6)', color: 'var(--c-text-2)' }}>{c.filename}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </HudCard>

      {selectedFiles.length > 0 && (
        <HudCard variant="glass">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <span className="hud-eyebrow hud-eyebrow-gold">Étape 2</span>
              <h3 className="font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 16 }}>
                Type de pièce par photo
              </h3>
            </div>
            <HudButton variant="secondary" size="sm" onClick={classify} disabled={classifying} leftIcon={<ScanSearch className={`w-3.5 h-3.5 ${classifying ? 'animate-pulse' : ''}`} />} data-testid="materialtheque-photos-classify">
              {classifying ? 'Détection…' : undetected.length ? `Détecter les pièces (${undetected.length})` : 'Re-détecter'}
            </HudButton>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {selectedFiles.map((c) => {
              const sel = selected[c.file_id] || {};
              return (
                <div key={c.file_id} className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--c-border)', background: 'var(--c-surface-1)' }} data-testid={`materialtheque-photo-card-${c.file_id}`}>
                  <div className="relative aspect-[4/3]">
                    <img src={fileDownloadUrl(c.file_id)} alt={c.filename} className="w-full h-full object-cover" loading="lazy" />
                    <button type="button" onClick={() => toggle(c)} className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.65)', color: '#fff' }} title="Retirer" data-testid={`materialtheque-photo-remove-${c.file_id}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                    {sel.label && (
                      <span className="absolute bottom-2 left-2 text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.6)', color: '#FDE68A' }}>
                        <Sparkles className="inline w-2.5 h-2.5 mr-1" />{sel.label}{sel.confidence ? ` · ${Math.round(sel.confidence * 100)}%` : ''}
                      </span>
                    )}
                  </div>
                  <div className="p-3 space-y-1.5">
                    <p className="text-[11px] truncate" style={{ color: 'var(--c-text-muted)' }} title={c.filename}>{c.filename}</p>
                    <select
                      value={sel.room_type || ''}
                      onChange={(e) => setRoom(c.file_id, e.target.value)}
                      className="w-full text-[13px] px-2 py-1.5 rounded-lg outline-none"
                      style={{ background: 'var(--c-surface-2, rgba(255,255,255,0.04))', border: `1px solid ${sel.room_type ? 'var(--c-border-accent)' : 'var(--c-warning)'}`, color: 'var(--c-text)' }}
                      data-testid={`materialtheque-photo-room-${c.file_id}`}
                    >
                      <option value="">— Pièce à définir —</option>
                      {roomTypes.map((rt) => <option key={rt.id} value={rt.id}>{rt.label}</option>)}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 flex items-center justify-between gap-3 flex-wrap pt-4" style={{ borderTop: '1px solid var(--c-border)' }}>
            <span className="text-[12px]" style={{ color: undetected.length ? 'var(--c-warning)' : 'var(--c-success)' }}>
              {undetected.length ? `${undetected.length} photo(s) sans pièce — elles seront classées « Autre pièce »` : <><Check className="inline w-3.5 h-3.5 mr-1" />Toutes les pièces sont renseignées</>}
            </span>
            <HudButton variant="primary" size="md" onClick={launch} disabled={launching || !selectedIds.length} leftIcon={<Sparkles className="w-4 h-4" />} data-testid="materialtheque-photos-launch">
              {launching ? 'Lancement…' : `Extraire les matériaux (${selectedIds.length})`}
            </HudButton>
          </div>
        </HudCard>
      )}

      {selectedFiles.length === 0 && (
        <div className="flex items-center gap-2 justify-center text-[12px] py-4" style={{ color: 'var(--c-text-faint)' }}>
          <Images className="w-4 h-4" /> Ajoutez des photos ou piochez dans les fichiers du projet pour continuer
        </div>
      )}
    </div>
  );
};

export default PhotoSourcePanel;
